import { cn } from '@/platform/lib/utils';
import {
    Button,
    Dropdown,
    FormGroup,
    Input,
    Segment,
    SegmentContent,
} from '@/platform/ui';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';

const valueTypes = [
    { id: 'field', name: 'Werknemersveld' },
    { id: 'parameter', name: 'Parameter' },
    { id: 'calculation', name: 'Berekening' },
    { id: 'custom', name: 'Vrije invoer' },
    { id: 'calculated', name: 'Berekende waarde' },
];

const Rule = ({ rule, onChange, onRemove, fields }) => {
    const operators = useMemo(() => {
        if (rule?.type === 'rule') {
            return [
                { id: '', name: '(Stop)' },
                { id: '*', name: 'Vermenigvuldigen (*)' },
                { id: '/', name: 'Delen door (/)' },
                { id: '-', name: 'Aftrekken (-)' },
                { id: '+', name: 'Optellen (+)' },
                { id: 'cap', name: 'Aftoppen' },
            ];
        }

        return [
            { id: '=', name: '=' },
            { id: '!=', name: '!=' },
            { id: '<', name: '<' },
            { id: '>', name: '>' },
            { id: '<=', name: '<=' },
            { id: '>=', name: '>=' },
        ];
    }, [rule.type]);

    const addChild = () => {
        const newChildren = [...(rule?.children || [])];

        newChildren.push({
            type: 'rule',
            value_type: 'field',
            field: '',
            operator: '',
            value: '',
            id: `new-${rule.id}_${Date.now()}`,
            parent_id: rule.id,
        });

        onChange(rule.id, 'children', newChildren);
    };

    const handleChildUpdate = (id, field, value) => {
        onChange(
            rule.id,
            'children',
            rule?.children?.map(child => {
                if (child.id === id) {
                    return {
                        ...child,
                        [field]: value,
                    };
                }

                return child;
            })
        );
    };

    const handleChildRemove = id => {
        onChange(
            rule.id,
            'children',
            rule?.children?.filter(child => child?.id !== id)
        );
    };

    return (
        <>
            <Segment className={cn(rule.parent_id && 'ml-8')}>
                <SegmentContent>
                    <div className='grid grid-cols-[1fr_auto] items-end gap-4'>
                        <FormGroup inline equal>
                            <Dropdown
                                label='Type waarde'
                                value={rule?.value_type || ''}
                                options={valueTypes}
                                onChange={value =>
                                    onChange(rule.id, 'value_type', value)
                                }
                            />
                            {rule?.value_type === 'field' && (
                                <Dropdown
                                    options={fields}
                                    label='Veld'
                                    value={rule?.field || ''}
                                    onChange={value =>
                                        onChange(rule.id, 'field', value)
                                    }
                                />
                            )}
                            {rule?.value_type === 'custom' && (
                                <Input
                                    label='Waarde'
                                    value={rule?.field || ''}
                                    onChange={event =>
                                        onChange(
                                            rule.id,
                                            'field',
                                            event.target.value
                                        )
                                    }
                                />
                            )}
                            {rule?.value_type === 'calculated' && (
                                <Dropdown
                                    options={[
                                        {
                                            id: 'employment',
                                            name: 'Dienstverband',
                                        },
                                    ]}
                                    label='Waarde'
                                    value={rule?.field || ''}
                                    onChange={value =>
                                        onChange(rule.id, 'field', value)
                                    }
                                />
                            )}
                            {!['field', 'custom', 'calculated'].includes(
                                rule?.value_type
                            ) && (
                                <Input
                                    label='UID'
                                    value={rule?.field || ''}
                                    onChange={event =>
                                        onChange(
                                            rule.id,
                                            'field',
                                            event.target.value
                                        )
                                    }
                                />
                            )}
                            <Dropdown
                                options={operators}
                                label='Operator'
                                placeholder={
                                    rule?.type === 'rule' ? '(Stop)' : null
                                }
                                value={rule?.operator || ''}
                                onChange={value =>
                                    onChange(rule.id, 'operator', value)
                                }
                            />
                            {rule?.type !== 'rule' && (
                                <Input
                                    name='value'
                                    label='Waarde'
                                    value={rule?.value || ''}
                                    onChange={event =>
                                        onChange(
                                            rule.id,
                                            'value',
                                            event.target.value
                                        )
                                    }
                                />
                            )}
                        </FormGroup>
                        <div className='flex gap-2'>
                            <Button
                                type='button'
                                variant='danger'
                                onClick={() => onRemove(rule.id)}
                            >
                                <TrashIcon className='size-4' />
                            </Button>
                            {rule?.type !== 'rule' && !rule?.parent_id && (
                                <Button
                                    type='button'
                                    variant='primary'
                                    onClick={addChild}
                                >
                                    <PlusIcon className='size-4' />
                                </Button>
                            )}
                        </div>
                    </div>
                </SegmentContent>
            </Segment>

            {rule?.children?.map(child => (
                <Rule
                    key={child.id}
                    rule={child}
                    onChange={handleChildUpdate}
                    onRemove={handleChildRemove}
                    fields={fields}
                />
            ))}
        </>
    );
};

export default Rule;
